import React, { useState } from 'react';
import { Heart, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Product {
  id: string;
  name: string;
  description?: string;
  price: number;
  category?: string;
  images?: string[];
  stock_quantity?: number;
  business_name?: string;
  rating?: number;
  is_featured?: boolean;
}

interface ProductCardProps {
  product: Product;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
}

const LETTER_COLORS: Record<string, [string, string]> = {
  A: ['#1a1a2e', '#e94560'], B: ['#16213e', '#0f3460'], C: ['#0f3460', '#533483'],
  D: ['#533483', '#e94560'], E: ['#2b2d42', '#ef233c'], F: ['#1b4332', '#40916c'],
  G: ['#1d3557', '#457b9d'], H: ['#3d405b', '#81b29a'], I: ['#264653', '#2a9d8f'],
  J: ['#e9c46a', '#f4a261'], K: ['#023e8a', '#0077b6'], L: ['#370617', '#e85d04'],
  M: ['#212529', '#ffc800'], N: ['#1b263b', '#415a77'], O: ['#3a0ca3', '#7209b7'],
  P: ['#480ca8', '#b5179e'], Q: ['#560bad', '#f72585'], R: ['#10002b', '#e0aaff'],
  S: ['#03045e', '#00b4d8'], T: ['#004b23', '#70e000'], U: ['#370617', '#f48c06'],
  V: ['#1a1a1a', '#ffc800'], W: ['#0d1b2a', '#1b98e0'], X: ['#2d00f7', '#8900f2'],
  Y: ['#1a1a1a', '#f9c74f'], Z: ['#212121', '#ff6b6b'],
};

/** Red dot + label for missing fields */
const Missing: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex items-center gap-1 text-[10px] text-light-subtle dark:text-dark-subtle">
    {label}
    <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" title="Missing" />
  </span>
);

const LetterThumbnail: React.FC<{ name: string; category?: string }> = ({ name, category }) => {
  const letter = (name?.[0] || '?').toUpperCase();
  const [bg, accent] = LETTER_COLORS[letter] || ['#1a1a1a', '#ffc800'];
  return (
    <div
      className="w-full h-full flex flex-col items-center justify-center gap-1"
      style={{ background: `linear-gradient(135deg, ${bg} 0%, ${accent}33 100%)` }}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10" style={{ background: accent }} />
        <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full opacity-10" style={{ background: accent }} />
      </div>
      <div
        className="relative w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
        style={{ background: `${accent}22`, border: `2px solid ${accent}55` }}
      >
        <span className="text-2xl font-black" style={{ color: accent }}>{letter}</span>
      </div>
      {category && (
        <span className="relative text-[9px] font-bold uppercase tracking-widest opacity-60 text-white mt-0.5">
          {category}
        </span>
      )}
    </div>
  );
};

const ProductCard: React.FC<ProductCardProps> = ({ product, isFavorite = false, onToggleFavorite }) => {
  const [imgError, setImgError] = useState(false);
  const imageUrl = product.images?.[0];
  const showThumbnail = !imageUrl || imgError;
  const inStock = (product.stock_quantity ?? 1) > 0;

  return (
    <Link
      to={`/products/${product.id}`}
      className="group block w-full rounded-xl overflow-hidden bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border shadow-sm hover:shadow-md transition-shadow duration-200"
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden">
        {showThumbnail
          ? <LetterThumbnail name={product.name} category={product.category} />
          : <img src={imageUrl} alt={product.name} onError={() => setImgError(true)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        }

        {!inStock && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="text-white text-xs font-bold bg-black/60 px-2 py-1 rounded-full">Sold Out</span>
          </div>
        )}

        {product.is_featured && (
          <span className="absolute top-2.5 left-2.5 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-golden text-dark-bg">
            Featured
          </span>
        )}

        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleFavorite?.(product.id); }}
          className="absolute top-2.5 right-2.5 w-7 h-7 flex items-center justify-center rounded-full bg-white/90 dark:bg-black/60 hover:scale-110 transition-transform"
        >
          <Heart size={13} className={isFavorite ? 'fill-red-500 text-red-500' : 'text-light-subtle dark:text-dark-subtle'} />
        </button>
      </div>

      {/* Info */}
      <div className="px-3 py-2.5 space-y-0.5">

        {/* Business name */}
        {product.business_name
          ? <p className="text-[10px] text-light-subtle dark:text-dark-subtle truncate">{product.business_name}</p>
          : <Missing label="Business" />
        }

        {/* Product name */}
        {product.name
          ? <p className="text-[13px] font-semibold text-light-text dark:text-dark-text truncate leading-snug">{product.name}</p>
          : <Missing label="Name" />
        }

        {/* Category */}
        {!product.category && <Missing label="Category" />}

        {/* Price + Rating */}
        <div className="flex items-center justify-between pt-1">
          {product.price
            ? <p className="text-sm font-bold text-primary dark:text-golden">KES {product.price.toLocaleString()}</p>
            : <Missing label="Price" />
          }
          {product.rating != null && product.rating > 0
            ? (
              <span className="flex items-center gap-0.5 text-[11px] font-semibold text-amber-500">
                <Star size={11} className="fill-amber-400 text-amber-400" />
                {product.rating.toFixed(1)}
              </span>
            )
            : <Missing label="Rating" />
          }
        </div>

      </div>
    </Link>
  );
};

export default ProductCard;
